import { useCallback, useEffect, useRef, useState } from 'react';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router-dom';
import { findNextViewableAt, Order, requiresAttention, shouldBeShown } from '../../Models';
import { useSoundNotifications } from '../useSoundNotifications';
import useDesktopNotifications from '../useDesktopNotifications';
import { getNotificationConfig } from './getNotificationConfig';

const THROTTLE_MS = 5000;
const EXPIRY_MS = 30 * 60 * 1000;
const STORAGE_KEY_PREFIX = 'notified:';
const FIRST_RENDER_KEY = 'notifications:first-render';

const getStorageKey = (orderId: string, state: string, userId: string) =>
    `${STORAGE_KEY_PREFIX}${orderId}:${state}:${userId}`;

const hasBeenNotifiedRecently = (orderId: string, state: string, userId: string): boolean => {
    const item = sessionStorage.getItem(getStorageKey(orderId, state, userId));
    if (!item) return false;

    const timestamp = parseInt(item, 10);
    return !Number.isNaN(timestamp) && Date.now() - timestamp < EXPIRY_MS;
};

const markAsNotifiedNow = (orderId: string, state: string, userId: string) => {
    sessionStorage.setItem(getStorageKey(orderId, state, userId), Date.now().toString());
};

const isFirstRenderEver = (): boolean => !sessionStorage.getItem(FIRST_RENDER_KEY);
const markFirstRenderComplete = () => {
    sessionStorage.setItem(FIRST_RENDER_KEY, 'true');
};

export function useOrdersNotifications(orders: Order[], userId: string) {
    const navigate = useNavigate();
    const soundNotifications = useSoundNotifications();
    const desktopNotifications = useDesktopNotifications();
    const lastNotificationTimestamp = useRef(new Date(0));
    const timeoutRef = useRef<NodeJS.Timeout | null>(null);
    const [forceRender, setForceRender] = useState(0);

    const notify = useCallback(
        (order: Order) => {
            if (hasBeenNotifiedRecently(order.id, order.state, userId)) {
                return;
            }

            const now = dayjs();
            if (now.diff(dayjs(lastNotificationTimestamp.current)) < THROTTLE_MS) {
                return;
            }

            const config = getNotificationConfig(order);
            if (!config) return;

            const { title, body, playSound } = config;

            desktopNotifications.showNotification(title, body, () =>
                navigate(`/orders/${order.id}`, { replace: true }),
            );
            if (playSound) soundNotifications.playNotification('order');
            markAsNotifiedNow(order.id, order.state, userId);
            lastNotificationTimestamp.current = now.toDate();
        },
        [desktopNotifications, navigate, soundNotifications, userId],
    );

    const notifyIfNeeded = useCallback(() => {
        if (!orders.length) return;

        if (isFirstRenderEver()) {
            markFirstRenderComplete();
            orders
                .filter((o) => shouldBeShown(o, userId))
                .forEach((o) => markAsNotifiedNow(o.id, o.state, userId));
            return;
        }

        const interestingOrders = orders.filter(
            (o) =>
                (o.state === 'emitted' && shouldBeShown(o, userId)) ||
                (o.state !== 'emitted' && requiresAttention(o)) ||
                (o.state === 'fulfilled' && hasBeenNotifiedRecently(o.id, 'approved', userId)),
        );
        if (!interestingOrders.length) {
            return;
        }

        const orderToNotify = interestingOrders[0];
        notify(orderToNotify);
    }, [orders, notify, userId]);

    const scheduleNextNotificationCheck = useCallback(() => {
        const nextNotificationTime = findNextViewableAt(orders, userId);
        if (!nextNotificationTime) return; // Nothing to schedule

        const delay = dayjs(nextNotificationTime).diff(dayjs());
        if (delay <= 0) {
            return;
        }
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
            timeoutRef.current = null; // fully handles scenarios where might be reassigned before execution
        }
        timeoutRef.current = setTimeout(() => {
            setForceRender((prev) => prev + 1);
        }, delay);
    }, [orders, userId, setForceRender]);

    useEffect(() => {
        notifyIfNeeded();
        scheduleNextNotificationCheck();

        return () => {
            if (timeoutRef.current) clearTimeout(timeoutRef.current);
        };
    }, [orders, userId, notifyIfNeeded, scheduleNextNotificationCheck, forceRender]);
}
